import React, { useEffect, useState } from "react";
import { MessageSquareQuote, ShieldCheck, Star, MapPin } from "lucide-react";
import SectionHeading from "@/components/common/SectionHeading";
import { Reveal, Stagger, StaggerItem } from "@/components/common/Reveal";
import api from "@/lib/api";

const Stars = ({ n = 5 }) => (
  <div className="flex gap-0.5" aria-label={`${n} out of 5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`h-4 w-4 ${i <= n ? "fill-copper text-copper" : "text-[#D9E0E4]"}`} />
    ))}
  </div>
);

// Reviews are entered by the business through the admin panel — never fabricated.
// Shows an honest placeholder until genuine, permission-cleared reviews exist.
export default function ReviewsPlaceholder() {
  const [reviews, setReviews] = useState(null);

  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => setReviews([]));
  }, []);

  const hasReviews = reviews && reviews.length > 0;

  return (
    <section className="bg-alt border-y border-[#D9E0E4]" data-testid="reviews-section">
      <div className="container-page py-20 lg:py-28">
        <SectionHeading
          eyebrow="Feedback"
          title="Business relationships built on clear communication"
          intro="Feedback from businesses we coordinate with across local and international markets, published with their permission."
        />

        {reviews === null ? (
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-48 rounded-lg border border-[#D9E0E4] bg-white animate-pulse" />
            ))}
          </div>
        ) : hasReviews ? (
          <Stagger className={`mt-12 grid gap-6 ${
            reviews.length === 1 ? "max-w-xl mx-auto"
            : reviews.length === 2 ? "md:grid-cols-2 max-w-3xl mx-auto"
            : "md:grid-cols-2 lg:grid-cols-3"
          }`}>
            {reviews.map((r) => (
              <StaggerItem key={r.id}>
                <figure className="h-full bg-white border border-[#D9E0E4] rounded-lg p-6 flex flex-col" data-testid={`review-card-${r.id}`}>
                  <div className="flex items-center justify-between">
                    <MessageSquareQuote className="h-6 w-6 text-copper" />
                    {r.rating ? <Stars n={r.rating} /> : null}
                  </div>
                  <blockquote className="mt-4 text-[15px] text-navy leading-relaxed flex-1">“{r.quote}”</blockquote>
                  <figcaption className="mt-5 pt-4 border-t border-[#EAF1F4]">
                    <p className="text-[14px] font-semibold text-navy">{r.name}</p>
                    <p className="text-[12.5px] text-muted-foreground">
                      {[r.role, r.company].filter(Boolean).join(", ")}
                    </p>
                    {r.country && (
                      <p className="mt-1.5 inline-flex items-center gap-1 text-[11.5px] font-medium text-teal">
                        <MapPin className="h-3.5 w-3.5 text-copper" /> {r.country}
                      </p>
                    )}
                  </figcaption>
                </figure>
              </StaggerItem>
            ))}
          </Stagger>
        ) : (
          <Reveal className="mt-10">
            <div className="rounded-lg border border-dashed border-[#C3CDD3] bg-white p-10 lg:p-14 text-center max-w-2xl">
              <div className="mx-auto h-12 w-12 rounded-full bg-[#EAF1F4] flex items-center justify-center">
                <ShieldCheck className="h-6 w-6 text-teal" />
              </div>
              <p className="mt-5 text-[16px] text-navy font-medium">
                Verified customer feedback will appear here after written publication permission is obtained.
              </p>
              <p className="mt-2 text-[14px] text-muted-foreground">
                We do not display fabricated testimonials or unverified ratings.
              </p>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}
